import type { MedicalLinkVoiceWidgetPublicConfig, WidgetConfig, WidgetInstance } from '../types/widget'
import { showVoiceNavMountError } from './mountHostStyles'
import { getWidgetPanelControls } from './widgetControls'
import { mountVoiceNavWidget } from './mount'

/** gTTS test loop is on unless the host explicitly passes `enableGttsTest: false`. */
export function isGttsTestEnabled(value: boolean | undefined): boolean {
  return value !== false
}

function withWidgetDefaults(config: WidgetConfig): WidgetConfig {
  return {
    ...config,
    enableGttsTest: isGttsTestEnabled(config.enableGttsTest),
  }
}

let currentInstance: WidgetInstance | null = null

function panelApi(): Pick<WidgetInstance, 'open' | 'close' | 'toggle'> {
  return {
    open: () => getWidgetPanelControls()?.open(),
    close: () => getWidgetPanelControls()?.close(),
    toggle: () => getWidgetPanelControls()?.toggle(),
  }
}

export function initVoiceNavWidget(config: WidgetConfig): WidgetInstance {
  if (currentInstance) currentInstance.destroy()

  const mounted = mountVoiceNavWidget(withWidgetDefaults(config))
  currentInstance = {
    destroy: () => {
      try {
        mounted.destroy()
      } finally {
        currentInstance = null
      }
    },
    ...panelApi(),
  }
  return currentInstance
}

/**
 * Initialize from `window.MedicalLinkVoiceWidgetConfig` shape (client handoff package).
 */
export function initFromMedicalLinkPublicConfig(cfg: MedicalLinkVoiceWidgetPublicConfig): WidgetInstance {
  const full: WidgetConfig = {
    target: cfg.target,
    defaultPanelOpen: cfg.defaultPanelOpen,
    position: cfg.position,
    title: cfg.title,
    placeholder: cfg.placeholder,
    theme: cfg.theme,
    websocketUrl: cfg.websocketUrl,
    apiBaseUrl: cfg.apiBaseUrl,
    clientId: cfg.clientId,
    tenantId: cfg.tenantId,
    patientContext: cfg.patientContext,
    piBearerToken: cfg.piBearerToken,
    loggedUserId: cfg.loggedUserId,
    medCenterId: cfg.medCenterId,
    enableGttsTest: isGttsTestEnabled(cfg.enableGttsTest),
  }
  return initVoiceNavWidget(full)
}

export function destroyVoiceNavWidget() {
  if (!currentInstance) return
  currentInstance.destroy()
  currentInstance = null
}

export function openVoiceNavPanel() {
  getWidgetPanelControls()?.open()
}

export function closeVoiceNavPanel() {
  getWidgetPanelControls()?.close()
}

export function toggleVoiceNavPanel() {
  getWidgetPanelControls()?.toggle()
}
